import { alpha, Box, useTheme } from "@mui/material";
import { forwardRef, useImperativeHandle, useState } from "react";
import {
	DOT_EASING,
	DOT_SIZE,
	DOT_STEP,
	getDotOffset,
	getPillAnimation,
	getRowWidth,
	PILL_WIDTH,
} from "web_myplann/Dashboard/Utils/carouselDotsLayout";
import type { SwipeableComponentRef } from "web_myplann/Shared/Hooks/useSwipeable";
import { DASHBOARD_TRANSITION_DURATION_MS } from "web_myplann/Utils/WebViewModel";

type Props = {
	length: number;
	currentValue?: number;
	onClick: (val: number, direction?: "left" | "right") => void;
};

export const DashboardCarouselDots = forwardRef<SwipeableComponentRef, Props>(
	({ length, currentValue = 0, onClick }, ref) => {
		const { palette, shape } = useTheme();
		const [pillAnimation, setPillAnimation] = useState("none");
		const [lastValue, setLastValue] = useState(currentValue);

		if (currentValue !== lastValue) {
			setPillAnimation(getPillAnimation(lastValue, currentValue));
			setLastValue(currentValue);
		}

		useImperativeHandle(ref, () => ({
			swipeLeft: () => {
				onClick(currentValue > 0 ? currentValue - 1 : length - 1, "left");
			},
			swipeRight: () => {
				onClick(currentValue > length - 2 ? 0 : currentValue + 1, "right");
			},
		}));

		if (length === 0) {
			return null;
		}

		return (
			<Box position="relative" width={getRowWidth(length)} height={DOT_SIZE}>
				{Array.from({ length }).map((_, dotIndex) => (
					<Box
						// biome-ignore lint/suspicious/noArrayIndexKey: Static carousel dots with no unique identifier
						key={dotIndex}
						position="absolute"
						top={0}
						left={0}
						height={DOT_SIZE}
						width={DOT_SIZE}
						borderRadius={shape.pillRadius}
						bgcolor={alpha(palette.darkmodeText.white, 0.4)}
						sx={{
							cursor: "pointer",
							WebkitTapHighlightColor: "transparent",
							transform: `translateX(${getDotOffset(dotIndex, currentValue)}px)`,
							transition: `transform ${DASHBOARD_TRANSITION_DURATION_MS}ms ${DOT_EASING}`,
							willChange: "transform",
						}}
						onClick={() => onClick(dotIndex)}
					/>
				))}
				<Box
					position="absolute"
					top={0}
					left={0}
					height={DOT_SIZE}
					width={PILL_WIDTH}
					borderRadius={shape.pillRadius}
					bgcolor={palette.darkmodeText.white}
					sx={{
						pointerEvents: "none",
						transform: `translateX(${currentValue * DOT_STEP}px)`,
						animation: pillAnimation,
						willChange: "transform",
					}}
				/>
			</Box>
		);
	},
);
